import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { authenticateAndFetchData, retryFn } from "@lib/apis";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
} from "recharts";
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Stack,
    Divider,
    ButtonBase,
    useTheme,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import { format, parseISO } from "date-fns";

interface RawGraphRow {
    date: string;
    source_option__key: string;
    count: number;
}

const COLOR_PALETTE = [
    "#008FFB",
    "#00E396",
    "#FFC107",
    "#FF4560",
    "#775DD0",
    "#26A69A",
    "#6CD5EC",
    "#546E7A",
    "#D10CE8",
    "#3F51B5",
];

export function OnboardingSourceChart() {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [days, setDays] = useState<string>("all");
    const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({});

    const { data: graphResponse, isLoading } = useQuery({
        queryKey: ["adminOnboardingGraphData", days],
        queryFn: () => authenticateAndFetchData(`/admin/onboarding-question-graph-filter/?days=${days}`),
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    // 1. Pivot flat backend rows into time-series chart data
    const { chartData, seriesKeys } = useMemo(() => {
        const rawRows: RawGraphRow[] = graphResponse?.data?.rows || [];
        if (!rawRows.length) {
            return { chartData: [], seriesKeys: [] };
        }

        const keysSet = new Set<string>();
        const groupedByDate = new Map<string, Record<string, any>>();

        rawRows.forEach((row) => {
            const dateKey = row.date;
            const sourceKey = row.source_option__key || "Unknown";
            keysSet.add(sourceKey);

            if (!groupedByDate.has(dateKey)) {
                groupedByDate.set(dateKey, { date: dateKey });
            }
            groupedByDate.get(dateKey)![sourceKey] = row.count;
        });

        const keys = Array.from(keysSet);

        // Ensure all missing keys in a date row default to 0
        const sortedData = Array.from(groupedByDate.values())
            .map((entry) => {
                keys.forEach((key) => {
                    if (entry[key] === undefined) {
                        entry[key] = 0;
                    }
                });
                return entry;
            })
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        return { chartData: sortedData, seriesKeys: keys };
    }, [graphResponse]);

    // 2. Initialize visible series when keys are loaded
    useEffect(() => {
        if (seriesKeys.length > 0) {
            setVisibleSeries((prev) => {
                const next = { ...prev };
                seriesKeys.forEach((key) => {
                    if (next[key] === undefined) {
                        next[key] = true;
                    }
                });
                return next;
            });
        }
    }, [seriesKeys]);

    const toggleSeries = (key: string) => {
        setVisibleSeries((prev) => {
            if (prev[key]) {
                const activeCount = Object.values(prev).filter(Boolean).length;
                if (activeCount <= 1) return prev; // Prevent disabling all series
            }
            return { ...prev, [key]: !prev[key] };
        });
    };

    const getSeriesColor = (index: number) => COLOR_PALETTE[index % COLOR_PALETTE.length];

    if (isLoading) {
        return (
            <Paper variant="outlined" sx={{ p: 4, mb: 3, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={28} />
            </Paper>
        );
    }

    if (!chartData.length) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                bgcolor: "background.paper",
                borderColor: "divider",
            }}
        >
            {/* Header: Title, Days Filter & Legend Toggles */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
                sx={{ mb: 3 }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                        Discovery Source Activity
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            sx={{
                                height: 34,
                                fontSize: "0.85rem",
                                borderRadius: 1.5,
                            }}
                        >
                            <MenuItem value="7">Last 7 days</MenuItem>
                            <MenuItem value="30">Last 30 days</MenuItem>
                            <MenuItem value="100">Last 100 days</MenuItem>
                            <MenuItem value="200">Last 200 days</MenuItem>
                            <MenuItem value="500">Last 500 days</MenuItem>
                            <MenuItem value="1000">Last 1000 days</MenuItem>
                            <MenuItem value="all">All Time</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
                    {seriesKeys.map((key, index) => {
                        const color = getSeriesColor(index);
                        const isVisible = !!visibleSeries[key];

                        return (
                            <ButtonBase
                                key={key}
                                onClick={() => toggleSeries(key)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 1,
                                    opacity: isVisible ? 1 : 0.4,
                                    transition: "opacity 0.2s ease-in-out",
                                    "&:hover": { opacity: 1 },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: color,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    fontWeight={500}
                                    color="text.secondary"
                                    sx={{ textTransform: "capitalize" }}
                                >
                                    {key.replace(/_/g, " ")}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Stack>
            </Stack>

            {/* Area Chart Container */}
            <Box sx={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                            {seriesKeys.map((key, index) => {
                                const color = getSeriesColor(index);
                                return (
                                    <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={color} stopOpacity={isDark ? 0.6 : 0.4} />
                                        <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                                    </linearGradient>
                                );
                            })}
                        </defs>

                        <CartesianGrid
                            vertical={false}
                            stroke={theme.palette.divider}
                            strokeDasharray="8 3"
                            strokeOpacity={0.7}
                        />

                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            minTickGap={30}
                            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                            tickFormatter={(value) => format(parseISO(value), "MMM d")}
                        />

                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                            tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                            domain={[0, (dataMax: number) => Math.max(5, Math.ceil(dataMax * 1.2))]}
                        />

                        <RechartsTooltip
                            cursor={false}
                            content={({ active, payload, label }) => {
                                if (active && payload && payload.length && label) {
                                    return (
                                        <Paper
                                            elevation={4}
                                            sx={{
                                                bgcolor: "background.paper",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 2,
                                                minWidth: 180,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                fontWeight={600}
                                                color="text.primary"
                                                sx={{ display: "block", px: 1.5, py: 1 }}
                                            >
                                                {format(parseISO(label as string), "EEEE, MMM d, yyyy")}
                                            </Typography>
                                            <Divider />
                                            <Stack spacing={1} sx={{ p: 1.5 }}>
                                                {payload.map((item) => (
                                                    <Stack
                                                        key={item.dataKey}
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        gap={2}
                                                    >
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Box
                                                                sx={{
                                                                    width: 8,
                                                                    height: 8,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: item.color,
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ textTransform: "capitalize" }}
                                                            >
                                                                {String(item.dataKey).replace(/_/g, " ")}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="caption" fontWeight={600} color="text.primary">
                                                            {item.value}
                                                        </Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    );
                                }
                                return null;
                            }}
                        />

                        {seriesKeys.map((key, index) => {
                            if (!visibleSeries[key]) return null;
                            const color = getSeriesColor(index);

                            return (
                                <Area
                                    key={key}
                                    dataKey={key}
                                    type="monotone"
                                    fill={`url(#gradient-${key})`}
                                    stroke={color}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4, strokeWidth: 0 }}
                                />
                            );
                        })}
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Paper>
    );
}